import React from 'react';
import { useMutation } from '@tanstack/react-query';
import ReportAPI from 'apis/ReportAPI';
import LogAPI from 'apis/LogAPI';
import useTrackingPage from 'hooks/useTrackingPage';
import csvFileExporter from 'utils/CSVFileExporter';
import textFileExporter from 'utils/TextFileExporter';
import { exportToDocx } from 'utils/Exporter';
import { loadChunkedCodesVCodesReport } from 'utils/chunkedReportLoader';
import { loadAllFilteredExcerpts, loadAllExcerpts } from 'utils/excerptLoader';
import exportCodesVCodesMapper from 'mappers/exports/ExportCodesVCodesMapper';
import exportCodesVTranscriptsMapper from 'mappers/exports/ExportCodesVTranscriptsMapper';
import exportCodesVDescriptorMapper from 'mappers/exports/ExportCodesVDescriptorMapper';
import exportExcerptMapper from 'mappers/exports/ExportExcerptMapper';
import exportCodesCSVMapper from 'mappers/exports/ExportCodesCSVMapper';
import exportCodesMapper from 'mappers/exports/ExportCodesMapper';
import exportDemographicsCSVMapper from 'mappers/exports/ExportDemographicsCSVMapper';
import exportTranscriptsVDescriptorMapper from 'mappers/exports/ExportTranscriptsVDescriptorMapper';
import exportCodingsCSVMapper from 'mappers/exports/ExportCodingsCSVMapper';
import QualAPI from 'utils/QualAPI';

export function useExportCodesVCodesMutation(projectData, codesData) {
  // export-code-x-code-matrix is logged server-side in CodesCodesReportController.
  // Exporting all codes fans out into multiple chunked requests, so the frontend
  // flags only the first chunk (logExport) and the controller logs once. The page
  // travels via X-Delve-Page.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async (codeIds = null) => {
      // ONLY codes v codes matrix uses chunked loading for large datasets
      if (codeIds) {
        // Specific code IDs provided - single request, so flag it directly
        return ReportAPI.getCodesVCodesReport(projectData.id, codeIds, 'or', { page, logExport: true });
      } else {
        // Use chunking when exporting all codes (can be 200+ codes)
        return loadChunkedCodesVCodesReport(projectData.id, codesData, { page });
      }
    },
    onSuccess: (reportData) => {
      const exportResults = exportCodesVCodesMapper(projectData, codesData, reportData);

      if (exportResults) {
        csvFileExporter(exportResults.projectName, exportResults.matrix);
      }
    }
  });
}

export function useExportCodesVTranscriptsMutation(projectData, codesData) {
  // export-code-x-transcript-matrix is logged server-side in
  // TranscriptCodesReportController; the page travels there via X-Delve-Page.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async () => {
      // Simple API call - NO chunking needed
      return ReportAPI.getTranscriptCodesReport(projectData.id, { page });
    },
    onSuccess: (reportData) => {
      const exportResults = exportCodesVTranscriptsMapper(projectData, codesData, reportData);

      if (exportResults) {
        csvFileExporter(exportResults.projectName, exportResults.matrix);
      }
    }
  });
}

export function useExportCodesVDescriptorMutation(projectData, codesData, descriptorsData) {
  // export-code-x-descriptor-matrix is logged server-side in
  // DescriptorCodesReportController; the page travels there via X-Delve-Page.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async () => {
      // Simple API call - NO chunking needed
      return ReportAPI.getDescriptorVCodesReport(projectData.id, { page });
    },
    onSuccess: (reportData) => {
      const exportResults = exportCodesVDescriptorMapper(projectData, codesData, descriptorsData, reportData);

      if (exportResults) {
        csvFileExporter(exportResults.projectName, exportResults.matrix);
      }
    }
  });
}

// Non-matrix exports

// Simple code export (no filters needed)
export function useExportCodesCsvMutation(projectData, codesData) {
  // Built entirely client-side, so it's logged via /api/logs.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async () => {
      // Use the updated exportCodesCSVMapper
      return exportCodesCSVMapper(projectData, codesData);
    },
    onSuccess: (exportResults) => {
      if (exportResults) {
        csvFileExporter(exportResults.projectName, exportResults.codeText);
        LogAPI.logEvent('export-codes-csv', { page });
      }
    }
  });
}

// Demographics export - counts of transcripts per descriptor option
export function useExportDemographicsCsvMutation(projectData) {
  // export-descriptors-csv is logged server-side in DemographicsReportController
  // (the export round-trips that endpoint); the page travels via X-Delve-Page.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async () => {
      // Simple API call - the report already includes descriptor names and choices
      return ReportAPI.getDemographicsReport(projectData.id, { page });
    },
    onSuccess: (reportData) => {
      const exportResults = exportDemographicsCSVMapper(projectData, reportData);

      if (exportResults) {
        csvFileExporter(exportResults.projectName, exportResults.demographicsText);
      }
    }
  });
}

// Transcript x Descriptor export - one row per transcript, one column per
// descriptor, cells hold the selected option names
export function useExportTranscriptsVDescriptorMutation(projectData, transcripts, descriptorsData) {
  // Built client-side from the selections batch endpoint, so it's logged via /api/logs.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async () => {
      const response = await QualAPI.fetchProjectSelections(projectData.id);
      return exportTranscriptsVDescriptorMapper(projectData, transcripts, descriptorsData, response.data);
    },
    onSuccess: (exportResults) => {
      if (exportResults) {
        csvFileExporter(exportResults.projectName, exportResults.transcriptsText);
        LogAPI.logEvent('export-transcript-x-descriptor', { page });
      }
    }
  });
}

// Transcript export - fetches transcript data on demand
export function useExportTranscriptMutation() {
  // Client-side download, logged via /api/logs. Triggered from both the Reports
  // page and the transcript header, so the page is resolved per-surface.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async (transcriptId) => {
      // Fetch the transcript data only when export is triggered
      const QualAPI = await import('utils/QualAPI').then(m => m.default);
      const response = await QualAPI.getTranscript(transcriptId);
      return response.data;
    },
    onSuccess: (transcript) => {
      const name = transcript.name || 'Untitled';
      textFileExporter(name, transcript.body);
      LogAPI.logEvent('export-transcript', { page });
    }
  });
}

// Filtered snippets export (needs filters from context)
export function useExportSnippetsMutation(projectData, descriptorsData, codesData, usersData, transcripts, filters) {
  // Built client-side, logged via /api/logs. Triggered from the Reports page and
  // the Analysis export modal, so the page is resolved per-surface.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async () => {
      // 1. Fetch selections if descriptors exist (lightweight)
      let selectionsData = null;
      if (descriptorsData?.descriptors?.length > 0) {
        const response = await QualAPI.fetchProjectSelections(projectData.id);
        selectionsData = response.data;
      }

      // 2. Load excerpts only when export button is clicked (heavyweight)
      const excerpts = await loadAllFilteredExcerpts(projectData.id, filters);

      // 3. Use transcripts passed as parameter
      const transcriptsList = transcripts || [];

      // 4. Pass everything to the mapper including selections
      return exportExcerptMapper(projectData, excerpts, descriptorsData, codesData, usersData, transcriptsList, selectionsData);
    },
    onSuccess: (exportResults) => {
      if (exportResults) {
        csvFileExporter(exportResults.projectName, exportResults.exportExcerpts);
        LogAPI.logEvent('export-snippets-csv', { page });
      }
    }
  });
}

// Long-format codings export - one row per coding (single code + coder),
// other snippet columns duplicated per row
export function useExportCodingsMutation(projectData, descriptorsData, codesData, usersData, transcripts, filters) {
  // Built client-side like the snippets export, logged via /api/logs.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async () => {
      let selectionsData = null;
      if (descriptorsData?.descriptors?.length > 0) {
        const response = await QualAPI.fetchProjectSelections(projectData.id);
        selectionsData = response.data;
      }

      const excerpts = await loadAllFilteredExcerpts(projectData.id, filters);
      const transcriptsList = transcripts || [];

      return exportCodingsCSVMapper(projectData, excerpts, descriptorsData, codesData, usersData, transcriptsList, selectionsData);
    },
    onSuccess: (exportResults) => {
      if (exportResults) {
        csvFileExporter(exportResults.projectName, exportResults.codingsText);
        LogAPI.logEvent('export-codings-csv', { page });
      }
    }
  });
}

// Codebook with excerpts export (NO FILTERS - exports all excerpts)
export function useExportToWordMutation(projectData, codesData, transcripts) {
  // Built client-side (docx), logged via /api/logs. Triggered from the Reports
  // page and the Codes header, so the page is resolved per-surface.
  const page = useTrackingPage();
  return useMutation({
    mutationFn: async () => {
      // Load ALL excerpts (no filters) using excerptLoader
      const allExcerpts = await loadAllExcerpts(projectData.id);

      // Use transcripts passed as parameter
      const transcriptsList = transcripts || [];

      // Use the updated exportCodesMapper
      return exportCodesMapper(projectData, codesData, allExcerpts, transcriptsList);
    },
    onSuccess: (exportObject) => {
      if (exportObject) {
        exportToDocx(exportObject.projectName, exportObject.fileName, exportObject.codes);
        LogAPI.logEvent('export-codes-doc', { page });
      }
    }
  });
}